import React, { useCallback, useMemo, useState, useEffect } from 'react'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import yaml from 'js-yaml'
import deepmerge from 'deepmerge'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import {CopyToClipboard} from 'react-copy-to-clipboard'

import Form from 'components/form/Form'
import Table from 'components/table/SimpleTable'
import Window from 'components/dialog/Window'

import snackbarActions from 'store/modules/snackbar'
import adminActions from 'store/modules/admin'
import adminSelectors from 'store/selectors/admin'
import bookingformActions from 'store/modules/bookingform'
import bookingformSelectors from 'store/selectors/bookingform'
import routerActions from 'store/modules/router'
import routerSelectors from 'store/selectors/router'
import projectSelectors from 'store/selectors/project'

import dateUtils from 'shared/src/date'

import {
  makeid,
} from 'utils/id'

import {
  fullUrl,
} from 'utils/url'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  buttons: {
    marginTop: theme.spacing(4),
  },
  copyButton: {
    margin: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  configField: {
    width: '100%',
  }
}))

const SCHEMA = [{
  id: 'name',
  title: 'Name',
  helperText: 'Enter the name of the booking form',
  validate: {
    type: 'string',
    methods: [
      ['required', 'The name is required'],
    ]
  }
},{
  id: 'url',
  title: 'URL',
  helperText: 'Enter the url of the booking form',
  validate: {
    type: 'string',
    methods: [
      ['required', 'The url is required'],
    ]
  }
}, {
  id: 'yaml_config',
  title: 'Config YAML',
  helperText: 'The config YAML for the booking form',
  rows: 10,
  component: 'textarea',
  validate: {
    type: 'string',
    methods: [
      ['required', 'The config is required'],
    ]
  }
}, {
  id: 'meta.config_repo_path',
  title: 'Config Path',
  prependText: 'https://github.com/binocarlos/booking-configs/',
  helperText: 'A file path to the booking form config in the config repo',
}]

const CONFIG_TABLE_FIELDS = [{
  title: 'Date',
  name: 'date_title',
}]

const HANDLERS = {
  validate: (values) => {
    const errors = {}
    if(values.config) {
      try {
        yaml.load(values.config)
      } catch (e) {
        errors.config = e.toString()
      }
    }
    return errors
  }
}

const BookingFormConfig = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ viewingConfig, setViewingConfig ] = useState(null)
  const error = useSelector(adminSelectors.saveBookingform.error)
  
  const { id } = useSelector(routerSelectors.params)
  const data = useSelector(bookingformSelectors.editBookingForm)
  const configs = useSelector(bookingformSelectors.configs)
  const project = useSelector(projectSelectors.currentProject)

  const useSchema = useMemo(() => {
    return SCHEMA.map(item => {
      if(item.id == 'url') {
        return Object.assign({}, item, {
          prependText: `${fullUrl(project)}/`,
        })
      }
      else {
        return item
      }
    })
  }, [
    SCHEMA,
    project,
  ])

  const initialValues = deepmerge({
    name: '',
    yaml_config: '',
    url: makeid(6).toLowerCase(),
  }, data || {})

  const onCancel = useCallback(() => {
    dispatch(routerActions.navigateTo('admin.bookingforms.list'))
  })

  const onSubmit = useCallback(async (payload) => {
    const result = await dispatch(adminActions.saveBookingform(id, payload))
    if(result && id == 'new') {
      dispatch(routerActions.navigateTo('admin.bookingforms.list'))
    }
  }, [
    id,
  ])

  const onCopy = useCallback(() => {
    dispatch(snackbarActions.setSuccess('copied to clipboard'))
  }, [])

  const getConfigActions = useCallback((item) => {
    return (
      <>
        <Button
          size="small"
          variant="outlined"
          onClick={ () => setViewingConfig(item) }
        >
          View
        </Button>
      </>
    )
  })

  const closeConfigDialog = useCallback(() => {
    setViewingConfig(null)
  }, [

  ])

  const configData = useMemo(() => {
    return configs.map(config => {
      return Object.assign({}, config, {
        date_title: dateUtils.timeTitle(new Date(config.created_at))
      })
    })
  }, [
    configs,
  ])

  useEffect(() => {
    if(id == 'new') {
      dispatch(bookingformActions.setConfigs([]))
    }
    else {
      dispatch(bookingformActions.loadAdminBookingforms())
      dispatch(bookingformActions.loadAdminBookingformConfigs(id))
    }
  }, [
    id,
  ])

  if(id != 'new' && !data || (data && data.id != id)) return null

  return (
    <div className={ classes.root }>
      <Grid container spacing={ 4 }>
        <Grid item xs={ 12 } md={ 8 }>
          <Paper className={ classes.paper }>
            <Typography gutterBottom variant="h6">
              Booking Form: Config
            </Typography>
            <Form
              schema={ useSchema }
              handlers={ HANDLERS }
              error={ error }
              initialValues={ initialValues }
              onSubmit={ onSubmit }
            >
              {
                ({
                  isValid,
                  onSubmit,
                }) => {
                  return (
                    <div className={ classes.buttons }>
                      <Button
                        className={ classes.actionButton }
                        variant="contained"
                        onClick={ onCancel }
                      >
                        Cancel
                      </Button>
                      <Button
                        className={ classes.actionButton }
                        variant="contained"
                        color="primary"
                        onClick={ onSubmit }
                      >
                        Save
                      </Button>
                    </div>
                  )
                }
              }
            </Form>
          </Paper>
        </Grid>
        <Grid item xs={ 12 } md={ 4 }>
          <Paper className={ classnames(classes.paper, classes.marginTop) }>
            <Typography gutterBottom variant="h6">
              Config History
            </Typography>
            <Table
              hideHeader
              data={ configData }
              fields={ CONFIG_TABLE_FIELDS }
              getActions={ getConfigActions }
            />
          </Paper>
        </Grid>
      </Grid>
      {
        viewingConfig && (
          <Window
            open
            onCancel={ closeConfigDialog }
            withCancel
            buttons={
              <CopyToClipboard
                text={ viewingConfig.yaml_config }
                onCopy={ onCopy }
              >
                <Button
                  type="button"
                  variant="contained"
                  className={ classes.copyButton }
                >
                  Copy to clipboard
                </Button>
              </CopyToClipboard>
            }
          >
            <TextField
              className={ classes.configField }
              label={ `Config: ${dateUtils.timeTitle(new Date(viewingConfig.created_at))}` }
              multiline
              rows={10}
              value={viewingConfig.yaml_config}
              disabled
              variant="outlined"
            />
          </Window>
        )
      }
    </div>
  )
}

export default BookingFormConfig